/**
 * CL-388J — Portal auth error copy and session cleanup helpers.
 */

import { clearAdminReturnPath, consumePortalEmailLoginPending } from "./adminReturnIntent";
import { clearPortalInviteLandingProjectId } from "./engineerInviteOnboardingModel";
import {
  CLIENT_PORTAL_PICKER_DONE_KEY,
  CLIENT_PORTAL_WORKSPACE_KEY,
} from "./clientPortalAccess";

function safeStr(value) {
  return String(value ?? "").trim();
}

function codeOf(err) {
  return safeStr(err?.code).toLowerCase();
}

/**
 * Friendly Firebase / Firestore errors for portal login, signup, and data load.
 * Does not reveal whether an email account exists.
 */
export function prettyPortalFirebaseError(err) {
  const code = codeOf(err);
  if (
    code.includes("auth/invalid-credential") ||
    code.includes("auth/wrong-password") ||
    code.includes("auth/user-not-found")
  ) {
    return "Email or password is incorrect.";
  }
  if (code.includes("auth/invalid-email")) {
    return "That email address looks invalid.";
  }
  if (code.includes("auth/user-disabled")) {
    return "This account has been disabled. Contact your project administrator.";
  }
  if (code.includes("auth/too-many-requests")) {
    return "Too many sign-in attempts. Please wait a few minutes and try again.";
  }
  if (code.includes("auth/network-request-failed")) {
    return "Network error. Check your connection and try again.";
  }
  if (code.includes("auth/email-already-in-use")) {
    return "That email is already in use. Try logging in instead.";
  }
  if (code.includes("auth/weak-password")) {
    return "Password is too weak. Please use at least 6 characters.";
  }
  if (code.includes("permission-denied")) {
    return "Permission denied by Firestore rules. (This is a site config issue.)";
  }
  return safeStr(err?.message) || "Something went wrong.";
}

/** Login-specific alias (enumeration-safe credential failures). */
export function prettyPortalLoginError(err) {
  return prettyPortalFirebaseError(err);
}

/**
 * Invite read during signup — Firestore blocks get when invite is already used.
 */
export function prettyPortalInviteReadError(err) {
  if (codeOf(err).includes("permission-denied")) {
    return "Invalid or already-used invite code.";
  }
  return prettyPortalFirebaseError(err);
}

function normalizeInviteCode(value) {
  return safeStr(value).toUpperCase();
}

/**
 * Parse invite code from `?invite=` or `?code=` query params (signup deep link).
 * @param {string} [search]
 */
export function parseInviteCodeFromLocationSearch(search = "") {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get("invite") || params.get("code") || "";
    return normalizeInviteCode(raw);
  } catch {
    return "";
  }
}

/**
 * Signed-out /client-portal must not surface leftover Firestore permission-denied
 * from in-flight signed-in loads (users/{uid}, realEstate, leftover projects).
 * There is no public site-config document — do not open a collection for this.
 */
export function shouldSurfaceSignedInPortalError(authLike) {
  const current = authLike?.currentUser;
  return Boolean(current && !current.isAnonymous);
}

/** Clear portal session hints on sign-out (workspace choice, admin return path). */
export function clearPortalAuthSessionState() {
  clearAdminReturnPath();
  consumePortalEmailLoginPending();
  clearPortalInviteLandingProjectId();
  try {
    sessionStorage.removeItem(CLIENT_PORTAL_WORKSPACE_KEY);
    sessionStorage.removeItem(CLIENT_PORTAL_PICKER_DONE_KEY);
  } catch {
    // ignore
  }
}
